// Names as Second Life had them: a first name typed and a last name chosen
// from the list the grid offers, the two together an account's name for
// good. The grid checks everything again; these are the checks worth making
// before anything is sent, and the grid's public questions about names.

import type { AgentTitleUpdate, ProfileReply } from "./messages";

/** The first name's length, as the grid has it unless it says otherwise. */
export const FIRST_NAME_MIN = 2;
export const FIRST_NAME_MAX = 16;

/** What is wrong with a first name as typed, in plain words, or null. */
export function firstNameProblem(raw: string, min = FIRST_NAME_MIN, max = FIRST_NAME_MAX): string | null {
  const first = raw.trim();
  if (!first) return "Type your first name.";
  if (!/^[A-Za-z]+$/.test(first)) return "Letters A to Z only: no spaces, digits or accents.";
  if (first.length < min) return `At least ${min} letters.`;
  if (first.length > max) return `At most ${max} letters.`;
  return null;
}

/**
 * A first name as the grid will keep it: a capital first and the rest as
 * typed, so McKenzie stays McKenzie, except that one typed all in capitals
 * is given small letters after the first.
 */
export function tidyFirstName(raw: string): string {
  const first = raw.trim();
  if (!first) return "";
  const rest = first.length > 2 && first === first.toUpperCase() ? first.slice(1).toLowerCase() : first.slice(1);
  return first[0].toUpperCase() + rest;
}

/** The name one signs in with as one word: 'nova.starwick'. */
export function loginHandle(first: string, last: string): string {
  return `${first.trim()}.${last.trim()}`.toLowerCase();
}

/** A name typed to sign in, its spaces tidied; the grid does the rest. */
export function tidyLogin(raw: string): string {
  return raw.trim().replace(/\s+/g, " ");
}

/**
 * The account name as it is shown under a display name: 'Nova Starwick'
 * as it is, and an older one-word username marked as one, '@ebalaijr'.
 */
export function accountNameLine(accountName: string): string {
  return accountName.includes(" ") ? accountName : `@${accountName}`;
}

// The names revision of the protocol appends an account name to
// AgentTitleUpdate, sent when somebody is renamed, and to one's own
// ProfileReply when a display name may next change. From a grid that does
// not send them they read as nothing.

/** The account name somebody now has, sent when staff rename them; empty for no change. */
export function renamedTo(update: AgentTitleUpdate): string {
  return update.username;
}

/** When one's own display name may next be changed, in unix seconds; 0 for now. */
export function displayNameNextChange(profile: ProfileReply): number {
  return Number(profile.display_name_next_change);
}

/** What the grid offers to choose from, from GET /api/names/last. */
export interface LastNames {
  names: string[];
  firstMin: number;
  firstMax: number;
}

/**
 * The last names a new account may choose from, A to Z. Null for a grid
 * without a list, which still makes accounts by username; a grid that does
 * not answer is an error, as it is for everything else.
 */
export async function fetchLastNames(gridUrl: string): Promise<LastNames | null> {
  const res = await fetch(`${gridUrl}/api/names/last`, { headers: { accept: "application/json" } });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`grid answered ${res.status}`);
  const body = (await res.json()) as { names?: string[]; first_name?: { min?: number; max?: number } };
  const names = (body.names ?? []).filter((n) => typeof n === "string" && n.length > 0);
  names.sort((a, b) => a.localeCompare(b));
  return {
    names,
    firstMin: body.first_name?.min ?? FIRST_NAME_MIN,
    firstMax: body.first_name?.max ?? FIRST_NAME_MAX,
  };
}

/** The grid's answer to whether a name is free, from GET /api/names/check. */
export interface NameCheck {
  available: boolean;
  /** As it would be kept: 'Nova Starwick'. */
  name: string;
  /** What it signs in as: 'nova.starwick'. */
  login: string;
  /** In plain words, whichever way it went. */
  message: string;
  /** A few last names still free with this first name. */
  others: string[];
}

/**
 * Ask the grid whether a first and last name are free to take: its answer,
 * or, when it would not say (asked too often, mostly), why not in its words.
 */
export async function checkName(gridUrl: string, first: string, last: string, signal?: AbortSignal): Promise<NameCheck | string> {
  const query = new URLSearchParams({ first, last });
  const res = await fetch(`${gridUrl}/api/names/check?${query}`, { headers: { accept: "application/json" }, signal });
  const body = (await res.json().catch(() => ({}))) as Partial<NameCheck> & { error?: string };
  if (!res.ok) return body.error || "The name could not be checked just now. It is checked again when you make the account.";
  return {
    available: body.available === true,
    name: body.name ?? "",
    login: body.login ?? "",
    message: body.message ?? "",
    others: Array.isArray(body.others) ? body.others.filter((n) => typeof n === "string") : [],
  };
}

/** Whole years from a YYYY-MM-DD birth date to a day; null if it is not one or is later. */
export function ageOn(birth: string, today: Date): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birth.trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const born = new Date(Date.UTC(y, mo - 1, d));
  if (born.getUTCFullYear() !== y || born.getUTCMonth() !== mo - 1 || born.getUTCDate() !== d) return null;
  const ty = today.getUTCFullYear();
  const tm = today.getUTCMonth() + 1;
  const td = today.getUTCDate();
  if (y > ty || (y === ty && (mo > tm || (mo === tm && d > td)))) return null;
  let years = ty - y;
  if (tm < mo || (tm === mo && td < d)) years--;
  return years;
}

/** Accounts are for adults, as on the website. */
export const ADULT_AGE = 18;

export function birthDateProblem(birth: string, today = new Date()): string | null {
  const age = ageOn(birth, today);
  if (age === null) return "Give your date of birth.";
  if (age > 120) return "Check your date of birth.";
  if (age < ADULT_AGE) return `You must be ${ADULT_AGE} or older.`;
  return null;
}

export function emailProblem(raw: string): string | null {
  const email = raw.trim();
  const at = email.indexOf("@");
  if (at < 1 || at !== email.lastIndexOf("@")) return "That does not look like an email address.";
  const domain = email.slice(at + 1);
  if (!domain.includes(".") || domain.startsWith(".") || domain.endsWith(".")) return "That does not look like an email address.";
  if (/[\s<>,;"\\]/.test(email)) return "That does not look like an email address.";
  return null;
}
